import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaBriefcase, FaArrowLeft, FaSearch } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import usePageTitle from '../hooks/usePageTitle';

const AllCareers = () => {
  usePageTitle('Semua Bidang Karir');
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    supabase.from('alumni').select('id, name, job, institution, photo_url')
      .order('id', { ascending: false })
      .then(({ data }) => setAlumni(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Case-insensitive job grouping
  const jobMap = {};
  const jobLabelMap = {};
  alumni.forEach(a => {
    if (a.job) {
      const normalized = a.job.trim().toLowerCase();
      if (!jobLabelMap[normalized]) jobLabelMap[normalized] = a.job.trim();
      if (!jobMap[normalized]) jobMap[normalized] = [];
      jobMap[normalized].push(a);
    }
  });

  const allJobs = Object.keys(jobMap)
    .map(k => ({ label: jobLabelMap[k], count: jobMap[k].length, alumni: jobMap[k] }))
    .sort((a, b) => b.count - a.count);

  const totalAlumni = alumni.length;

  const filtered = search
    ? allJobs.filter(j => j.label.toLowerCase().includes(search.toLowerCase()))
    : allJobs;

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/statistik" className="inline-flex items-center gap-2 text-gray-300 hover:text-white text-sm font-medium mb-4 transition-colors">
            <FaArrowLeft className="text-xs" /> Kembali ke Statistik
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <FaBriefcase className="text-gold-400" /> Semua Bidang Karir Alumni
          </h1>
          <p className="text-gray-300">Daftar lengkap seluruh bidang karir alumni PIMNAS — {allJobs.length} bidang dari {totalAlumni} alumni</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="relative max-w-md mb-8">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Cari bidang karir..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition shadow-sm"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <FaBriefcase className="text-4xl mx-auto mb-4 opacity-50" />
            <p>Tidak ada bidang karir ditemukan</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(({ label, count, alumni: members }, i) => {
              const pct = totalAlumni > 0 ? Math.round((count / totalAlumni) * 100) : 0;
              return (
                <div key={label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 text-sm font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <h3 className="text-base font-semibold text-gray-900">{label}</h3>
                    </div>
                    <span className="text-sm text-gray-500 font-medium">{count} alumni ({pct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
                    <div
                      className="bg-gradient-to-r from-primary-500 to-gold-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    ></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {members.map(m => (
                      <Link
                        key={m.id}
                        to={`/alumni/${m.id}`}
                        className="inline-flex items-center gap-2 bg-gray-50 hover:bg-primary-50 text-sm text-gray-700 hover:text-primary-700 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        {m.photo_url ? (
                          <img src={m.photo_url} alt={m.name} className="w-5 h-5 rounded-full object-cover" />
                        ) : (
                          <span className="w-5 h-5 rounded-full bg-primary-200 text-primary-700 text-[10px] font-bold flex items-center justify-center">
                            {m.name?.charAt(0)}
                          </span>
                        )}
                        {m.name}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllCareers;
